import React, { useCallback, useEffect, useState } from 'react';
import {
	List,
	Datagrid,
	TextField,
	DateField,
	Edit,
	Create,
	SimpleForm,
	TextInput,
	NumberInput,
	SelectInput,
	required,
	useRecordContext,
	useGetList,
	useNotify,
	useRefresh,
} from 'react-admin';
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Grid,
	IconButton,
	TextField as MuiTextField,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';

import {
	addPartnerWalletEntry,
	getPartnerWallet,
	isWalletEntryEditable,
	IWalletBalances,
	IWalletEntry,
} from '../../Services/partners';
import { EditWalletEntryDialog } from './EditWalletEntryDialog';
import { PartnerMembers } from './PartnerMembers';
import { PartnerClients } from './PartnerClients';
import { PartnerCommissions } from './PartnerCommissions';
import { RecomputeCommissionsDialog } from './RecomputeCommissionsDialog';

// ⚠️ Plus de « programme » ni de « niveau » de partenaire. Ces deux champs pilotaient le
// taux de commission ; c'est désormais l'AFFAIRE (l'attribution) qui porte son type et son
// taux — ils se règlent à l'association du client, sur la fiche du billing account.
//
// Les colonnes existent toujours en base, figées (`integrateur` / `pro`), le temps de
// vérifier que plus rien ne les lit. Les exposer ici aurait laissé un levier qui ne
// change plus rien : le pire des réglages, celui qu'on croit actif.

// #region PartnerList
export const PartnerList: React.FC = (props: any) => (
	<List {...props} sort={{ field: 'created_at', order: 'DESC' }}>
		<Datagrid rowClick="edit">
			<TextField source="name" label="Nom" />
			<TextField source="legalName" label="Raison sociale" />
			<TextField source="city" label="Ville" />
			<DateField source="createdAt" label="Créé le" />
		</Datagrid>
	</List>
);
// #endregion

const partnerFields = (
	<>
		<TextInput source="legalName" label="Raison sociale" fullWidth />
		<TextInput source="name" label="Nom d'affichage" validate={required()} fullWidth />
		<SelectInput
			source="status"
			label="Statut"
			choices={[
				{ id: 200, name: 'Actif' },
				{ id: 100, name: 'Désactivé' },
			]}
			helperText="Désactivé : plus aucun accès à l'espace partenaire ni au lien d'affiliation. Les commissions dues restent à régler."
			fullWidth
		/>
		<NumberInput source="overdraftLimit" label="Découvert autorisé (crédits)" fullWidth />
		<TextInput source="website" label="Site internet" fullWidth />
		<TextInput source="address" label="Adresse (rue)" multiline fullWidth />
		<TextInput source="zipCode" label="Code postal" fullWidth />
		<TextInput source="city" label="Ville" fullWidth />
		<TextInput source="country" label="Pays" fullWidth />
		<TextInput source="phone" label="Téléphone" fullWidth />
		<TextInput source="siret" label="SIRET" fullWidth />
		<TextInput source="vatIntracom" label="TVA intracommunautaire" fullWidth />
	</>
);

// #region StatTile
const StatTile: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
	<Card>
		<CardContent>
			<Typography variant="overline" color="text.secondary">
				{label}
			</Typography>
			<Typography variant="h5">{value}</Typography>
		</CardContent>
	</Card>
);
// #endregion

const walletEur = (x: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(x || 0);
const walletCatLabel: Record<string, string> = { granted: 'Crédit offert', purchased: 'Crédit acheté', feature: 'Consommation', adjustment: 'Ajustement' };

// #region PartnerWallet — soldes unifiés + historique + ajout de crédit (admin)
const PartnerWallet: React.FC<{ id: string }> = ({ id }) => {
	const notify = useNotify();
	const [bal, setBal] = useState<IWalletBalances | null>(null);
	const [entries, setEntries] = useState<IWalletEntry[]>([]);
	const [amount, setAmount] = useState('');
	const [label, setLabel] = useState('');
	const [saving, setSaving] = useState(false);
	const [recomputing, setRecomputing] = useState(false);
	const [editing, setEditing] = useState<IWalletEntry | null>(null);
	// Rafraîchit aussi le tableau des commissions (useGetList), en plus des soldes.
	const refresh = useRefresh();

	const load = useCallback(() => {
		getPartnerWallet(id)
			.then((r) => {
				setBal(r.balances);
				setEntries(r.entries || []);
			})
			.catch(() => {
				setBal(null);
				setEntries([]);
			});
	}, [id]);
	useEffect(() => {
		load();
	}, [load]);

	const add = async () => {
		const n = parseFloat(amount);
		if (!n) return;
		setSaving(true);
		try {
			await addPartnerWalletEntry(id, n, label);
			notify('Ligne wallet ajoutée', { type: 'success' });
			setAmount('');
			setLabel('');
			load();
		} catch (e: any) {
			notify(`Erreur : ${e?.message || e}`, { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	return (
		<Card sx={{ mt: 2 }}>
			<CardContent>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
					<Typography variant="h6">Wallet</Typography>
					<Button size="small" variant="outlined" onClick={() => setRecomputing(true)}>
						Recalculer les commissions
					</Button>
				</Box>
				{bal && (
					<Grid container spacing={2} sx={{ mb: 2 }}>
						<Grid item xs={6} sm={3}>
							<StatTile label="Dépensable" value={walletEur(bal.spendable)} />
						</Grid>
						<Grid item xs={6} sm={3}>
							<StatTile label="Retirable" value={walletEur(bal.retirable)} />
						</Grid>
						<Grid item xs={6} sm={3}>
							<StatTile label="Crédits offerts" value={walletEur(bal.creditsGranted)} />
						</Grid>
						<Grid item xs={6} sm={3}>
							<StatTile label="Consommé" value={walletEur(bal.debits)} />
						</Grid>
					</Grid>
				)}

				<Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2, flexWrap: 'wrap' }}>
					<MuiTextField
						size="small"
						label="Montant (crédits)"
						type="number"
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						sx={{ width: 170 }}
						helperText="> 0 = crédit offert, < 0 = débit"
					/>
					<MuiTextField size="small" label="Libellé" value={label} onChange={(e) => setLabel(e.target.value)} sx={{ flex: '1 1 220px' }} />
					<Button variant="contained" onClick={add} disabled={saving || !amount} sx={{ mt: '4px' }}>
						Ajouter
					</Button>
				</Box>

				{entries.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Aucun mouvement wallet (hors commissions).
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Date</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>Libellé</TableCell>
								<TableCell align="right">Montant</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{entries.map((e) => {
								const s = e.kind === 'debit' ? -e.amount : e.amount;
								return (
									<TableRow key={e.id}>
										<TableCell>{e.createdAt?.slice(0, 10)}</TableCell>
										<TableCell>
											<Chip label={walletCatLabel[e.category] || e.category} size="small" />
										</TableCell>
										<TableCell>{e.label || '—'}</TableCell>
										<TableCell align="right" sx={{ color: s < 0 ? 'error.main' : 'success.main', fontWeight: 700 }}>
											{s < 0 ? '' : '+'}
											{walletEur(s)}
										</TableCell>
										<TableCell align="right">
											{isWalletEntryEditable(e) && (
												<IconButton size="small" onClick={() => setEditing(e)} title="Modifier ou supprimer">
													<EditIcon fontSize="small" />
												</IconButton>
											)}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				)}
				<EditWalletEntryDialog partnerId={id} entry={editing} onClose={() => setEditing(null)} onSaved={load} />
				<RecomputeCommissionsDialog
					partnerId={id}
					open={recomputing}
					onClose={() => setRecomputing(false)}
					onApplied={() => {
						load();
						refresh();
					}}
				/>
			</CardContent>
		</Card>
	);
};
// #endregion

// #region PartnerContent — stats + contacts liés (colonne centrale)
const PartnerContent: React.FC = () => {
	const record = useRecordContext();
	const id = record?.id as string | undefined;

	const { total: nbOpp } = useGetList(
		'opportunities',
		{ pagination: { page: 1, perPage: 1 }, filter: { partner_id: id } },
		{ enabled: !!id }
	);
	const { total: nbClients } = useGetList(
		'attributions',
		{ pagination: { page: 1, perPage: 1 }, filter: { partner_id: id } },
		{ enabled: !!id }
	);

	if (!record) return null;

	return (
		<>
			<Grid container spacing={2} sx={{ mb: 2 }}>
				<Grid item xs={6} sm={6}>
					<StatTile label="Opportunités" value={nbOpp ?? 0} />
				</Grid>
				<Grid item xs={6} sm={6}>
					<StatTile label="Clients apportés" value={nbClients ?? 0} />
				</Grid>
			</Grid>

			{id && <PartnerClients partnerId={id} />}

			{id && <PartnerMembers partnerId={id} />}

			{id && <PartnerWallet id={id} />}

			{/* Juste sous le wallet, et pas ailleurs : le wallet donne des SOLDES, ce
			    tableau donne leur composition. Une somme fausse ne se diagnostique qu'en
			    descendant à la ligne — quel paiement, quel taux, quelle facture Stripe. */}
			{id && <PartnerCommissions partnerId={id} />}
		</>
	);
};
// #endregion

// #region PartnerEdit — form à droite, stats + contacts au centre
export const PartnerEdit: React.FC = (props: any) => (
	<Edit {...props} component="div">
		<Grid container spacing={2} sx={{ mt: 0 }}>
			<Grid item xs={12} md={8}>
				<PartnerContent />
			</Grid>
			<Grid item xs={12} md={4}>
				<Card>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							Édition
						</Typography>
						<SimpleForm>{partnerFields}</SimpleForm>
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	</Edit>
);
// #endregion

// #region PartnerCreate
export const PartnerCreate: React.FC = (props: any) => (
	<Create {...props}>
		<SimpleForm>{partnerFields}</SimpleForm>
	</Create>
);
// #endregion
